import React, { useRef, useEffect, useState, KeyboardEvent } from 'react';
import { __ } from '@wordpress/i18n';
import * as m from 'motion/react-m';
import { AnimatePresence } from 'motion/react';
import { formatTimeRange, formatCalendarDate } from '../utils/date-time';
import { useBookingStore } from '../store/useBookingStore';
import {
	useSelectedTime,
	useEffectiveDuration,
	useOriginalTime,
	useSelectedDate,
} from '../hooks/useBookingComputed';

interface TimeSlot {
	time: string;
	available: boolean;
	remainingSlots: number;
}

interface TimeSlotsProps {
	slots: TimeSlot[];
	blockSize: number;
	showSpotsLeft?: boolean;
	spotsLeftText?: string | null;
	isEntryEdit?: boolean;
}

interface GridDimensions {
	columns: number;
	rows: number;
}

export const TimeSlots: React.FC<TimeSlotsProps> = ({
	slots,
	blockSize,
	showSpotsLeft = false,
	spotsLeftText = null,
	isEntryEdit = false,
}) => {
	// Get state from store and hooks
	const selectedTime = useSelectedTime();
	const effectiveDuration = useEffectiveDuration();
	const originalTime = useOriginalTime();
	const selectedDate = useSelectedDate();
	const originalRange = useBookingStore((state) => state.originalRange);
	const setSelectedTime = useBookingStore((state) => state.setSelectedTime);
	const skipAnimations = useBookingStore((state) => state.skipAnimations);
	const showEndTimes = useBookingStore((state) => state.showEndTimes);

	const duration = effectiveDuration || blockSize;
	const gridRef = useRef<HTMLDivElement>(null);
	const buttonsRef = useRef<(HTMLButtonElement | null)[]>([]);
	const [gridDimensions, setGridDimensions] = useState<GridDimensions>({
		columns: 1,
		rows: 1,
	});

	// Watch grid layout changes
	useEffect(() => {
		if (!gridRef.current) {
			return;
		}

		const observer = new ResizeObserver(() => {
			if (!gridRef.current) {
				return;
			}

			// Get computed grid layout
			const gridStyle = window.getComputedStyle(gridRef.current);
			const columns = gridStyle.gridTemplateColumns.split(' ').length;
			const rows = Math.ceil(slots.length / columns);

			setGridDimensions({ columns, rows });
		});

		observer.observe(gridRef.current);
		return () => observer.disconnect();
	}, [slots.length]);

	useEffect(() => {
		// Reset buttons ref array when slots change
		buttonsRef.current = buttonsRef.current.slice(0, slots.length);
	}, [slots]);

	const handleKeyDown = (
		event: KeyboardEvent<HTMLButtonElement>,
		index: number
	) => {
		const { columns } = gridDimensions;
		const currentColumn = index % columns;
		const currentRow = Math.floor(index / columns);
		let nextIndex: number | null = null;

		switch (event.key) {
			case 'ArrowRight':
				if (currentColumn < columns - 1 && index < slots.length - 1) {
					// Move right if not at end of row and not last slot
					nextIndex = index + 1;
				} else if (
					currentColumn === columns - 1 ||
					index === slots.length - 1
				) {
					// Wrap to start of next row, or first slot if at last slot
					const nextRow = (currentRow + 1) * columns;
					if (nextRow < slots.length) {
						// Go to start of next row
						nextIndex = nextRow;
					} else {
						// Wrap to first slot
						nextIndex = 0;
					}
				}
				break;

			case 'ArrowLeft':
				if (currentColumn > 0) {
					// Move left if not at start of row
					nextIndex = index - 1;
				} else {
					// Wrap to end of previous row
					const previousRow = currentRow - 1;
					if (previousRow >= 0) {
						// Go to end of previous row
						nextIndex = Math.min(
							(previousRow + 1) * columns - 1,
							slots.length - 1
						);
					} else {
						// Wrap to last slot
						nextIndex = slots.length - 1;
					}
				}
				break;

			case 'ArrowDown':
				const downIndex = index + columns;
				if (downIndex < slots.length) {
					// Move down if there's a slot below
					nextIndex = downIndex;
				} else {
					// Wrap to top of same column
					nextIndex = index % columns;
				}
				break;

			case 'ArrowUp':
				const upIndex = index - columns;
				if (upIndex >= 0) {
					// Move up if there's a slot above
					nextIndex = upIndex;
				} else {
					// Wrap to bottom of same column
					const lastRowStart =
						Math.floor((slots.length - 1) / columns) * columns;
					const targetIndex = lastRowStart + currentColumn;
					nextIndex =
						targetIndex < slots.length
							? targetIndex
							: targetIndex - columns;
				}
				break;

			case 'Home':
				if (event.ctrlKey) {
					nextIndex = 0; // Start of grid
				} else {
					nextIndex = currentRow * columns; // Start of current row
				}
				break;

			case 'End':
				if (event.ctrlKey) {
					nextIndex = slots.length - 1; // End of grid
				} else {
					// End of current row, but don't go past last slot
					nextIndex = Math.min(
						(currentRow + 1) * columns - 1,
						slots.length - 1
					);
				}
				break;

			default:
				return;
		}

		if (nextIndex !== null && nextIndex >= 0 && nextIndex < slots.length) {
			event.preventDefault();
			buttonsRef.current[nextIndex]?.focus();
		}
	};

	// Format spots left text with placeholders
	const formatSpotsLeftText = (remainingSlots: number): string => {
		if (!spotsLeftText) {
			// Default text with i18n
			return remainingSlots === 1
				? __('1 spot left', 'gp-bookings')
				: // translators: %d: number of spots left
					__('%d spots left', 'gp-bookings').replace(
						'%d',
						String(remainingSlots)
					);
		}

		let text = spotsLeftText;

		// Replace {spots} placeholder
		text = text.replace(/{spots}/g, String(remainingSlots));

		// Handle [[plural|singular]] conditional text
		text = text.replace(
			/\[\[([^\|]+)\|([^\]]+)\]\]/g,
			(_, plural, singular) => {
				return remainingSlots === 1 ? singular : plural;
			}
		);

		return text;
	};

	if (slots.length === 0) {
		return (
			<p role="alert" className="gpb-booking-time-picker__empty">
				{__('No available times for this date.', 'gp-bookings')}
			</p>
		);
	}

	const slotVariants = {
		hidden: { opacity: 0, y: 10 },
		visible: { opacity: 1, y: 0 },
	};

	return (
		<div
			ref={gridRef}
			className="gpb-booking-time-picker__grid"
			role="grid"
			aria-label={__('Available times', 'gp-bookings')}
			aria-rowcount={gridDimensions.rows}
			aria-colcount={gridDimensions.columns}
		>
			<AnimatePresence>
				{slots.map((slot, index) => {
					// Compare time directly - both are in HH:MM format
					const isSelected = selectedTime === slot.time;

					// Only show original slot indicator if it's on the currently selected date
					const isOriginalSlot =
						originalTime === slot.time &&
						selectedDate &&
						originalRange?.from &&
						formatCalendarDate(selectedDate) ===
							formatCalendarDate(originalRange.from);

					const timeRange = formatTimeRange(
						slot.time,
						duration,
						showEndTimes
					);
					const row = Math.floor(index / gridDimensions.columns) + 1;
					const column = (index % gridDimensions.columns) + 1;

					const buttonRef = (el: HTMLButtonElement | null) => {
						buttonsRef.current[index] = el;
					};

					// In entry edit mode, treat all slots as available
					const isAvailable = isEntryEdit || slot.available;

					return (
						<m.button
							ref={buttonRef}
							type="button"
							key={slot.time}
							data-time={slot.time}
							className={`gpb-booking-time-picker__slot ${
								!isAvailable
									? 'gpb-booking-time-picker__slot--unavailable'
									: ''
							} ${
								isSelected
									? 'gpb-booking-time-picker__slot--selected'
									: ''
							} ${
								isOriginalSlot
									? 'gpb-booking-time-picker__slot--original'
									: ''
							}`}
							onClick={() =>
								isAvailable && setSelectedTime(slot.time)
							}
							disabled={!isAvailable}
							onKeyDown={(e) => handleKeyDown(e, index)}
							role="gridcell"
							aria-selected={isSelected}
							aria-label={
								isAvailable
									? isSelected
										? /* translators: 1: time range */
											__(
												'Selected time: %1$s',
												'gp-bookings'
											).replace('%1$s', timeRange)
										: /* translators: 1: time range */
											__(
												'Available time: %1$s',
												'gp-bookings'
											).replace('%1$s', timeRange)
									: /* translators: %s: time range */
										__(
											'Unavailable time: %s',
											'gp-bookings'
										).replace('%s', timeRange)
							}
							aria-rowindex={row}
							aria-colindex={column}
							tabIndex={
								isSelected || (!selectedTime && index === 0)
									? 0
									: -1
							}
							variants={slotVariants}
							initial={skipAnimations ? false : 'hidden'}
							animate="visible"
							transition={{
								type: skipAnimations ? false : 'tween',
								delay: skipAnimations ? 0 : index * 0.05,
								duration: skipAnimations ? 0 : 0.2,
							}}
						>
							<span className="gpb-booking-time-picker__slot-content">
								<span
									className="gpb-booking-time-picker__slot-time"
									aria-hidden="true"
								>
									{timeRange}
								</span>
								{showSpotsLeft &&
									isAvailable &&
									slot.remainingSlots > 0 && (
										<span
											className="gpb-booking-time-picker__slot-spots"
											aria-hidden="true"
										>
											{formatSpotsLeftText(
												slot.remainingSlots
											)}
										</span>
									)}
							</span>
						</m.button>
					);
				})}
			</AnimatePresence>
		</div>
	);
};
